import * as React from "react";
import { useQuery } from "@apollo/client";
import { Box, Grid, GridBaseProps, IconButton, Stack, Typography } from "@mui/material";
import DeleteMeetingButton from "../DeleteMeetingButton";
import { useNavigate } from "react-router-dom";
import { CheckIcon, CloseIcon, EditIcon } from "../icons";
import Section from "./Section";
import { createUtcMoment } from "../../tools";
import JoinMeetingSection from "./JoinMeetingSection";
import Html from "../Html";
import { valueOrEmpty } from "../tools";
import InvitationList from "./InvitationList";
import { Link as RouterLink } from "react-router-dom";
import SimpleDisplayField from "../SimpleDisplayField";
import { getCurrentInvitation } from "../joinMeetingTools";
import { MeetingViewQuery, MeetingViewDocument, MeetingViewQueryVariables } from "../queries.generated";
import { Duration } from "conference/components/DurationFields";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";

const datetimeFormat = "YYYY-MM-DD HH:mm";

const iconSize = 40;

const DetailField = ({
    children,
    label,
    size = { sm: 12, md: 6 },
}: {
    label: MessageDescriptor;
    children: React.ReactNode;
    size?: GridBaseProps["size"];
}) => (
    <Grid container size={size}>
        <Grid size={{ xs: 4, sm: 3, md: 4 }} style={{ display: "flex", paddingTop: "0.2em" }}>
            <Typography>
                <FormattedMessage {...label} />:
            </Typography>
        </Grid>
        <Grid size={{ xs: 8, sm: 9, md: 8 }}>
            <div style={{ marginLeft: iconSize, display: "inline-block" }}>{children}</div>
        </Grid>
    </Grid>
);

export default ({ id }: { id: number }) => {
    const navigate = useNavigate();
    const { data } = useQuery<MeetingViewQuery, MeetingViewQueryVariables>(MeetingViewDocument, {
        variables: { meetingId: id },
        fetchPolicy: "cache-and-network",
    });

    if (!data?.meetings) return null;

    const meeting = data.meetings.items[0];

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    "&:hover .controls": {
                        opacity: 1,
                    },
                    marginTop: "1em",
                }}
            >
                <Typography variant="h4">{meeting.summary}</Typography>
                <Box
                    className="controls"
                    sx={{
                        marginLeft: "1em",
                        opacity: 0.3,
                        transition: "0.2s opacity",
                    }}
                >
                    <IconButton size="small" component={RouterLink} to={`/meeting/edit/${id}`}>
                        <EditIcon />
                    </IconButton>
                    <DeleteMeetingButton name={meeting.summary} meetingId={id} onSuccess={() => navigate("/")} />
                </Box>
            </Box>
            <Section
                icon="info-circle"
                label={defineMessage({ id: "details", defaultMessage: "Details" })}
                containerStyle={{ paddingTop: 20, paddingLeft: 20 }}
            >
                <Grid container>
                    <DetailField label={defineMessage({ id: "time.start", defaultMessage: "Start time" })}>
                        <SimpleDisplayField value={createUtcMoment(meeting.startTime).local().format(datetimeFormat)} />
                    </DetailField>
                    <DetailField label={defineMessage({ id: "meetingLength.title", defaultMessage: "Length" })}>
                        <SimpleDisplayField value={<Duration value={meeting.length} />} />
                    </DetailField>
                    <DetailField label={defineMessage({ id: "meetings.roomId", defaultMessage: "Room ID" })}>
                        <SimpleDisplayField value={meeting.roomId} />
                    </DetailField>
                    <DetailField label={defineMessage({ id: "meetings.pinCode", defaultMessage: "Pin" })}>
                        <SimpleDisplayField value={meeting.pinCode} />
                    </DetailField>
                    <DetailField
                        label={defineMessage({
                            id: "allowUnmuteAll",
                            defaultMessage: "Allow for the participants to unmute themselves",
                        })}
                        size={{ sm: 12, md: 10 }}
                    >
                        {meeting.allowUnmute ? <CheckIcon /> : <CloseIcon />}
                    </DetailField>
                </Grid>
            </Section>
            <JoinMeetingSection meeting={meeting} invitation={getCurrentInvitation(data.invitations.items)} />
            <Section icon="list-ul" label={defineMessage({ id: "description", defaultMessage: "Description" })}>
                {meeting.description ? <Html html={meeting.description} /> : valueOrEmpty(meeting.description)}
            </Section>
            <Section
                icon="users"
                label={defineMessage({ id: "invitations.label", defaultMessage: "Invitations" })}
                containerStyle={{ paddingTop: 5 }}
            >
                <InvitationList invitations={data.invitations.items} />
            </Section>
        </Stack>
    );
};
